/**
 * App.tsx
 *
 * File-level:
 * - Top-level application component that sets up routing and global handlers.
 * - This variant keeps a single consistent content offset equal to the navigation height.
 *
 * Notes:
 * - Keeps a global unhandledrejection handler to reduce noisy console messages.
 * - Uses HashRouter for client-side routing (preinstalled react-router).
 */

import React, { useEffect } from 'react';
import { HashRouter, Route, Routes, useNavigate } from 'react-router';
import './sentry-blocker';
// import './runtime-api-base'; // DISABLED: This was intercepting API calls and using localStorage instead of backend
import './styles/theme.css';
import DevApiBanner from './components/DevApiBanner';
import Navigation from './components/Navigation';
import HomePage from './pages/Home';
import RegistrationPage from './pages/Registration';
import LoginPage from './pages/Login';
import ProfilePage from './pages/Profile';
import PartnerSearchPage from './pages/PartnerSearch';
import DashboardPage from './pages/Dashboard';
import PaymentPage from './pages/Payment';
import AdminDashboard from './pages/AdminDashboard';
import ViewProfile from './pages/ViewProfile';
import UserSearchProfile from './pages/UserSearchProfile';
import AdminLogin from './pages/AdminLogin';
import AdminHomeSettings from './pages/AdminHomeSettings';
import ProfileViewsEnhanced from './components/dashboard/ProfileViewsEnhanced';
import { Button } from './components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Toaster } from 'sonner';

/**
 * ProfileViewsRoute
 *
 * @description Inline route component that renders the active Profile Views page.
 * This avoids importing src/pages/ProfileViews which caused resolution failures
 * in some environments. Provides a simple Back button to return to /dashboard.
 */
function ProfileViewsRoute(): JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-4">
          <Button size="sm" className="bg-transparent border-gray-200 text-gray-700" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2 inline" /> Back
          </Button>
          <h1 className="text-xl font-semibold">Profile Views</h1>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border">
            <ProfileViewsEnhanced />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * handleUnhandledRejection
 *
 * @description Global handler for unhandled promise rejections.
 * Prevents noisy console errors caused by browser extensions or external message
 * channels that sometimes return `true` to indicate an async response but never
 * call sendResponse, which results in a common dev console error.
 *
 * @param e PromiseRejectionEvent
 */
function handleUnhandledRejection(e: PromiseRejectionEvent) {
  try {
    const reason = (e && (e.reason ?? '')) as any;
    const msg = typeof reason === 'string' ? reason : (reason && reason.message) || String(reason);
    if (msg && String(msg).includes('A listener indicated an asynchronous response')) {
      // Suppress this specific noisy extension error
      // eslint-disable-next-line no-console
      console.warn('[global] Suppressed extension message-channel error:', msg);
      e.preventDefault();
      return;
    }

    // Log other unhandled rejections as warnings and prevent default overlay behavior
    // eslint-disable-next-line no-console
    console.warn('[global] unhandled promise rejection:', reason);
    e.preventDefault();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[global] error in unhandledrejection handler', err);
  }
}

/**
 * App
 *
 * @description Main application component. Registers global handlers and routes.
 */
export default function App(): JSX.Element {
  useEffect(() => {
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <HashRouter>
      <div className="min-h-screen bg-gradient-to-br from-[#0b3b82] via-[#5f1c7c] to-[#ff2d87]">
        <Navigation />
        <DevApiBanner />
        <Toaster position="bottom-right" />

        {/*
          Single padding-top to offset the fixed navigation bar.
          Navigation has a fixed height (h-16). Use one pt-16 so there is no double gap.
          This ensures content starts directly below the navigation across all pages.
        */}
        <main className="pt-16">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/register" element={<RegistrationPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/search" element={<PartnerSearchPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin/home-settings" element={<AdminHomeSettings />} />
            <Route path="/profile/:userId" element={<ViewProfile />} />
            <Route path="/search-profile/:userId" element={<UserSearchProfile />} />
            {/* Use inline route to avoid import resolution issues */}
            <Route path="/profile-views" element={<ProfileViewsRoute />} />
          </Routes>
        </main>
      </div>
    </HashRouter>
  );
}
